import type { OpenedTrade } from "./types";
import { VybeOrderState, VybeOrderType, VybeUpdateType } from "./types";
import type { Quote } from "../subscribed-symbols/types";
import type { Symbol as VybeSymbol } from "../symbols/types";
import { VybeCalculationMode } from "../symbols/types";

const PENDING_ORDER_TYPES = new Set<number>([
  VybeOrderType.BuyLimit,
  VybeOrderType.SellLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.SellStop,
  VybeOrderType.BuyStopLimit,
  VybeOrderType.SellStopLimit,
]);

const ACTIVE_PENDING_STATES = new Set<number>([
  VybeOrderState.Started,
  VybeOrderState.Placed,
  VybeOrderState.Partial,
  VybeOrderState.RequestAdding,
  VybeOrderState.RequestModifying,
]);

const TRADE_UPSERT_UPDATE_TYPES = new Set<number>([
  VybeUpdateType.MarketOpen,
  VybeUpdateType.PendingOpen,
  VybeUpdateType.Started,
  VybeUpdateType.Filled,
]);

const TRADE_MODIFY_UPDATE_TYPES = new Set<number>([
  VybeUpdateType.MarketModify,
  VybeUpdateType.PendingModify,
]);

const TRADE_REMOVAL_UPDATE_TYPES = new Set<number>([
  VybeUpdateType.PendingClose,
  VybeUpdateType.MarketClose,
  VybeUpdateType.PartialClose,
  VybeUpdateType.OnStopLoss,
  VybeUpdateType.OnTakeProfit,
  VybeUpdateType.OnStopOut,
  VybeUpdateType.Expired,
  VybeUpdateType.Rejected,
  VybeUpdateType.MarketCloseBy,
]);

/**
 * States in which a MARKET order has not executed yet: it has been sent, or is working, but no volume has
 * been filled, so there is no position and no open price.
 *
 * isPendingTrade has always checked state; isMarketTrade did not, and that asymmetry is the bug. A market
 * order stuck in Placed satisfied "type is Buy/Sell and lots > 0" and was rendered as an OPEN POSITION with
 * an open price of 0. SR Global saw exactly that: engine order #3733, a 5-lot XAUUSD Buy left Placed and
 * never filled, shown as a position at 0.00 with +2,182,110 USD of phantom profit — and, because the row
 * counts toward the account, a broken free-margin figure that made real orders look as though they were
 * filling without margin. The engine's own positions list never contained #3733.
 *
 * Started (0) is deliberately NOT here. It is also the default for an unset numeric field, and a real open
 * position arrives carrying it, so excluding it would hide genuine positions — the opposite bug.
 */
const UNEXECUTED_MARKET_STATES = new Set<number>([
  VybeOrderState.Placed,
  VybeOrderState.RequestAdding,
]);

export function isMarketTrade(trade: OpenedTrade): boolean {
  return (
    (trade.orderType === VybeOrderType.Buy ||
      trade.orderType === VybeOrderType.Sell) &&
    trade.lots > 0 &&
    // Partial counts: some volume filled means a real position exists.
    !UNEXECUTED_MARKET_STATES.has(trade.state)
  );
}

export function isPendingTrade(trade: OpenedTrade): boolean {
  return (
    PENDING_ORDER_TYPES.has(trade.orderType) &&
    ACTIVE_PENDING_STATES.has(trade.state)
  );
}

export function isTradeDisplayRow(trade: OpenedTrade): boolean {
  return isMarketTrade(trade) || isPendingTrade(trade);
}

export function isTradeUpsertUpdateType(updateType: number): boolean {
  return TRADE_UPSERT_UPDATE_TYPES.has(updateType);
}

export function isTradeModifyUpdateType(updateType: number): boolean {
  return TRADE_MODIFY_UPDATE_TYPES.has(updateType);
}

export function isTradeRemovalUpdateType(updateType: number): boolean {
  return TRADE_REMOVAL_UPDATE_TYPES.has(updateType);
}

export function getMarketPositionSides(trades: OpenedTrade[]): {
  hasBuy: boolean;
  hasSell: boolean;
} {
  return trades.reduce<{ hasBuy: boolean; hasSell: boolean }>(
    (sides, trade) => {
      if (!isMarketTrade(trade)) {
        return sides;
      }

      if (trade.orderType === VybeOrderType.Buy) {
        sides.hasBuy = true;
      } else if (trade.orderType === VybeOrderType.Sell) {
        sides.hasSell = true;
      }

      return sides;
    },
    { hasBuy: false, hasSell: false }
  );
}

type PriceQuote = Pick<Quote, "bid" | "ask"> & Partial<Quote>;
type QuoteLookup = Map<string, PriceQuote | { quote?: PriceQuote } | undefined>;
type SymbolLookup = Map<string, VybeSymbol>;

export interface TradePnlInputs {
  trade: OpenedTrade;
  symbol?: VybeSymbol | null;
  quote?: PriceQuote | null;
  accountCurrency?: string | null;
  symbols?: SymbolLookup | null;
  quotes?: QuoteLookup | null;
  includeCosts?: boolean;
}

export interface ConversionRoute {
  symbols: string[];
}

export interface ConversionRateResult extends ConversionRoute {
  rate: number;
}

const MONEY_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function round(value: number, digits: number | undefined): number {
  const precision = Math.max(0, Math.min(digits ?? 8, 12));
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function isFinitePositive(value: number | undefined | null): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function normalizeCurrency(value?: string | null): string {
  return (value || "").trim().toUpperCase();
}

function getQuoteFromLookup(
  symbolName: string,
  quotes?: QuoteLookup | null
): PriceQuote | undefined {
  const entry = quotes?.get(symbolName);
  if (!entry) return undefined;
  if ("bid" in entry && "ask" in entry) return entry;
  return entry.quote;
}

function getRuntimeTickValue(
  trade: OpenedTrade,
  symbol?: VybeSymbol | null
): number {
  if (!symbol) return trade.contractSize || 1;

  if (trade.orderType === VybeOrderType.Buy) {
    return symbol.bidTickValue || symbol.tickValue || symbol.contractSize || 1;
  }

  return symbol.askTickValue || symbol.tickValue || symbol.contractSize || 1;
}

function hasSideSpecificTickValue(
  trade: OpenedTrade,
  symbol?: VybeSymbol | null
): boolean {
  if (!symbol) return false;
  return trade.orderType === VybeOrderType.Buy
    ? isFinitePositive(symbol.bidTickValue)
    : isFinitePositive(symbol.askTickValue);
}

function findSymbolForPair(pair: string, symbols?: SymbolLookup | null) {
  const normalizedPair = normalizeCurrency(pair);
  if (!symbols || !normalizedPair) return undefined;

  const exact = symbols.get(normalizedPair);
  if (exact) return exact;

  for (const symbol of symbols.values()) {
    const symbolName = normalizeCurrency(symbol.currency);
    const symbolCurrency = normalizeCurrency(symbol.symbolCurrency);
    if (
      symbolName.startsWith(normalizedPair) ||
      symbolCurrency.startsWith(normalizedPair)
    ) {
      return symbol;
    }
  }

  return undefined;
}

function getConversionLegRate(
  fromCurrency: string,
  toCurrency: string,
  amountSign: number,
  symbols?: SymbolLookup | null,
  quotes?: QuoteLookup | null
): ConversionRateResult | null {
  const from = normalizeCurrency(fromCurrency);
  const to = normalizeCurrency(toCurrency);
  if (!from || !to) return null;
  if (from === to) return { rate: 1, symbols: [] };

  const direct = findSymbolForPair(`${from}${to}`, symbols);
  if (direct) {
    const quote = getQuoteFromLookup(direct.currency, quotes);
    if (quote && isFinitePositive(quote.bid) && isFinitePositive(quote.ask)) {
      return {
        // Positive profit sells profit currency at bid; negative loss buys it back at ask.
        rate: amountSign >= 0 ? quote.bid : quote.ask,
        symbols: [direct.currency],
      };
    }
  }

  const inverse = findSymbolForPair(`${to}${from}`, symbols);
  if (inverse) {
    const quote = getQuoteFromLookup(inverse.currency, quotes);
    if (quote && isFinitePositive(quote.bid) && isFinitePositive(quote.ask)) {
      return {
        rate: amountSign >= 0 ? 1 / quote.ask : 1 / quote.bid,
        symbols: [inverse.currency],
      };
    }
  }

  return null;
}

export function getRequiredProfitConversionSymbols(
  profitCurrency: string | null | undefined,
  accountCurrency: string | null | undefined,
  symbols?: SymbolLookup | null
): string[] {
  const from = normalizeCurrency(profitCurrency);
  const to = normalizeCurrency(accountCurrency);
  if (!from || !to || from === to) return [];

  const direct = findSymbolForPair(`${from}${to}`, symbols);
  if (direct) return [direct.currency];

  const inverse = findSymbolForPair(`${to}${from}`, symbols);
  if (inverse) return [inverse.currency];

  if (from !== "USD" && to !== "USD") {
    const firstLeg =
      findSymbolForPair(`${from}USD`, symbols) ||
      findSymbolForPair(`USD${from}`, symbols);
    const secondLeg =
      findSymbolForPair(`USD${to}`, symbols) ||
      findSymbolForPair(`${to}USD`, symbols);

    return [firstLeg?.currency, secondLeg?.currency].filter(
      (symbolName): symbolName is string => Boolean(symbolName)
    );
  }

  return [];
}

export function resolveProfitConversionRate(
  profitCurrency: string | null | undefined,
  accountCurrency: string | null | undefined,
  profitAmount: number,
  symbols?: SymbolLookup | null,
  quotes?: QuoteLookup | null
): ConversionRateResult | null {
  const from = normalizeCurrency(profitCurrency);
  const to = normalizeCurrency(accountCurrency);
  const amountSign = profitAmount < 0 ? -1 : 1;
  if (!from || !to) return null;
  if (from === to) return { rate: 1, symbols: [] };

  const direct = getConversionLegRate(from, to, amountSign, symbols, quotes);
  if (direct) return direct;

  if (from !== "USD" && to !== "USD") {
    const toUsd = getConversionLegRate(from, "USD", amountSign, symbols, quotes);
    const fromUsd = getConversionLegRate("USD", to, amountSign, symbols, quotes);
    if (toUsd && fromUsd) {
      return {
        rate: toUsd.rate * fromUsd.rate,
        symbols: [...toUsd.symbols, ...fromUsd.symbols],
      };
    }
  }

  return null;
}

export function getTradeClosePrice(
  trade: Pick<OpenedTrade, "orderType" | "openPrice"> &
    Partial<Pick<OpenedTrade, "closePrice">>,
  quote?: Pick<Quote, "bid" | "ask"> | null
): number {
  if (quote && isFinitePositive(quote.bid) && isFinitePositive(quote.ask)) {
    return trade.orderType === VybeOrderType.Buy ? quote.bid : quote.ask;
  }

  return isFinitePositive(trade.closePrice) ? trade.closePrice : trade.openPrice;
}

export function getTradePriceChange(
  trade: Pick<OpenedTrade, "orderType" | "closePrice" | "openPrice">,
  quote?: Pick<Quote, "bid" | "ask"> | null
): number {
  const closePrice = getTradeClosePrice(trade, quote);
  return trade.orderType === VybeOrderType.Buy
    ? closePrice - trade.openPrice
    : trade.openPrice - closePrice;
}

export function calculateTradeRawProfit(
  trade: OpenedTrade,
  symbol?: VybeSymbol | null,
  closePrice = trade.closePrice
): number {
  if (!isMarketTrade(trade)) return 0;

  const isBuy = trade.orderType === VybeOrderType.Buy;
  const priceDiff = isBuy
    ? closePrice - trade.openPrice
    : trade.openPrice - closePrice;
  const lots = trade.lots || 0;
  const contractSize = symbol?.contractSize || trade.contractSize || 1;
  const positionSize = lots * contractSize;
  const precision = symbol?.precision ?? trade.digits;
  const calcMode = symbol?.calcMode;
  const runtimeTickValue = getRuntimeTickValue(trade, symbol);

  switch (calcMode) {
    case VybeCalculationMode.Forex:
    case VybeCalculationMode.CalcMode5: {
      const closeValue = round(positionSize * closePrice, precision);
      const openValue = round(positionSize * trade.openPrice, precision);
      return isBuy ? closeValue - openValue : openValue - closeValue;
    }
    case VybeCalculationMode.Futures:
    case VybeCalculationMode.ExchangeFutures:
    case VybeCalculationMode.ExchangeOption:
    case VybeCalculationMode.ExchangeMarginOption: {
      const tickValue = runtimeTickValue;
      const tickSize = symbol?.tickSize || 1;
      return (lots * priceDiff * tickValue) / tickSize;
    }
    case VybeCalculationMode.FORTSFutures: {
      const tickValue = symbol?.tickValue || 1;
      const tickSize = symbol?.tickSize || 1;
      const openValue = round((tickValue * trade.openPrice) / tickSize, precision);
      const closeValue = round((tickValue * closePrice) / tickSize, precision);
      return lots * (isBuy ? closeValue - openValue : openValue - closeValue);
    }
    case VybeCalculationMode.ExchangeStocks:
      return lots * priceDiff;
    case VybeCalculationMode.ExchangeBounds: {
      const faceValue = symbol?.faceValue || 1;
      const closeValue = round(
        (positionSize * closePrice * faceValue) / 100,
        precision
      );
      const openValue = round(
        (positionSize * trade.openPrice * faceValue) / 100,
        precision
      );
      return isBuy ? closeValue - openValue : openValue - closeValue;
    }
    case VybeCalculationMode.Collateral:
      return 0;
    default:
      return lots * priceDiff * runtimeTickValue;
  }
}

function usesAccountCurrencyTickValue(
  trade: OpenedTrade,
  symbol?: VybeSymbol | null
): boolean {
  if (!hasSideSpecificTickValue(trade, symbol)) return false;

  switch (symbol?.calcMode) {
    case VybeCalculationMode.Futures:
    case VybeCalculationMode.ExchangeFutures:
    case VybeCalculationMode.ExchangeOption:
    case VybeCalculationMode.ExchangeMarginOption:
    case VybeCalculationMode.CFD:
    case VybeCalculationMode.CFDIndex:
    case VybeCalculationMode.CFDLeverage:
    case undefined:
      return true;
    default:
      return false;
  }
}

export function getTradeAccountPnl(
  trade: Partial<OpenedTrade>,
  calculatedProfit?: number,
  includeCosts = true
): number {
  const profit =
    typeof calculatedProfit === "number" && Number.isFinite(calculatedProfit)
      ? calculatedProfit
      : trade.profit || 0;

  if (!includeCosts) return profit;

  return profit + (trade.swap || 0) + (trade.commission || 0) + (trade.fee || 0);
}

export function calculateTradeAccountPnl({
  trade,
  symbol,
  quote,
  accountCurrency,
  symbols,
  quotes,
  includeCosts = true,
}: TradePnlInputs): number {
  if (!isMarketTrade(trade)) return 0;

  const closePrice = getTradeClosePrice(trade, quote);
  const rawProfit = calculateTradeRawProfit(trade, symbol, closePrice);
  const alreadyAccountCurrency = usesAccountCurrencyTickValue(trade, symbol);
  const profitCurrency = alreadyAccountCurrency
    ? normalizeCurrency(accountCurrency)
    : normalizeCurrency(symbol?.profitCurrency) ||
      normalizeCurrency(accountCurrency);

  const conversion = resolveProfitConversionRate(
    profitCurrency,
    accountCurrency,
    rawProfit,
    symbols,
    quotes
  );

  const convertedProfit = conversion
    ? rawProfit * conversion.rate
    : trade.profit || rawProfit;
  return getTradeAccountPnl(trade, convertedProfit, includeCosts);
}

export function formatSignedPnl(value: number): string {
  if (!Number.isFinite(value)) return "--";
  const sign = value > 0 ? "+" : "";
  return `${sign}${MONEY_FORMATTER.format(value)}`;
}

export function formatTradeAccountPnl(
  tradeOrValue: Partial<OpenedTrade> | number,
  currency?: string | null
): string {
  const value =
    typeof tradeOrValue === "number"
      ? tradeOrValue
      : getTradeAccountPnl(tradeOrValue);
  const formatted = formatSignedPnl(value);
  return currency ? `${formatted} ${currency}` : formatted;
}
